import { useMutation, useQueryClient } from '@tanstack/react-query';

import { mediaFolderApi } from '../api/media-api';

import { mediaKeys } from './media-keys';

/** Membership changes show on every file (folderIds) and every count, so lists and details both refresh. */
function useRefreshFolders() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: mediaKeys.folders() }),
      queryClient.invalidateQueries({ queryKey: mediaKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: mediaKeys.details() }),
    ]);
}

export function useCreateMediaFolder() {
  const refresh = useRefreshFolders();
  return useMutation({
    mutationFn: (name: string) => mediaFolderApi.create({ name }),
    meta: { successMessage: 'media:toasts.folderCreated', silentError: true },
    onSuccess: refresh,
  });
}

export function useRenameMediaFolder() {
  const refresh = useRefreshFolders();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => mediaFolderApi.rename(id, { name }),
    meta: { successMessage: 'media:toasts.folderRenamed', silentError: true },
    onSuccess: refresh,
  });
}

export function useDeleteMediaFolder() {
  const refresh = useRefreshFolders();
  return useMutation({
    mutationFn: (id: string) => mediaFolderApi.remove(id),
    meta: { successMessage: 'media:toasts.folderDeleted' },
    onSuccess: refresh,
  });
}

/**
 * Adds the files to each folder; files already in a folder are left alone by the backend.
 * `silent` skips the toast, for uploads that land in the open folder on their own.
 */
export function useAddToMediaFolders({ silent = false }: { silent?: boolean } = {}) {
  const refresh = useRefreshFolders();
  return useMutation({
    mutationFn: ({ folderIds, mediaIds }: { folderIds: string[]; mediaIds: string[] }) =>
      Promise.all(folderIds.map((id) => mediaFolderApi.addItems(id, { mediaIds }))),
    meta: silent ? {} : { successMessage: 'media:toasts.addedToFolder' },
    onSuccess: refresh,
  });
}

export function useRemoveFromMediaFolder() {
  const refresh = useRefreshFolders();
  return useMutation({
    mutationFn: ({ folderId, mediaIds }: { folderId: string; mediaIds: string[] }) =>
      mediaFolderApi.removeItems(folderId, { mediaIds }),
    meta: { successMessage: 'media:toasts.removedFromFolder' },
    onSuccess: refresh,
  });
}
